import { FC, Suspense } from "react";
import { useFragment } from "react-relay";
import {
  Header as BaseHeader,
  Provider,
  useGlobalModalContext,
  WalletIcons,
  WalletProps,
} from "rujira.ui";
import "rujira.ui/src/scss/index.scss";

import clsx from "clsx";
import { Link } from "../../Gate";
import { PortfolioFragment$key } from "../../portfolio/__generated__/PortfolioFragment.graphql";
import { PortfolioFragment } from "../../portfolio/Portfolio";
import { totalValue } from "../../portfolio/utils";
import { usePreloadedAccountData } from "../../services/accountData";
import { useAccounts } from "../../services/accounts";
import { usePreloadedPendingDeposits } from "../../services/deposits";
import { usePreloadedBalanceData } from "./Balance";
import { Deposit } from "./Deposit";

export const WALLETS: WalletProps[] = [
  {
    key: "station",
    label: "Station",
    icon: <WalletIcons.StationText />,
  },
  {
    key: "ctrl",
    label: "Ctrl",
    provider: "Ctrl",
    icon: <WalletIcons.Ctrl className="color-white" />,
    url: "https://ctrl.xyz/download/",
  },
  {
    key: "brave",
    label: "Brave",
    provider: "Brave",
    icon: <WalletIcons.Brave />,
    url: "https://brave.com/wallet/",
  },
  {
    key: "coinbase",
    label: "Coinbase",
    provider: "Coinbase",
    icon: <WalletIcons.Coinbase />,
    url: "https://www.coinbase.com/en-gb/wallet",
  },
  {
    key: "daodao",
    label: "DaoDao",
    provider: "DaoDao",
    icon: <WalletIcons.Daodao className="color-white" />,
  },
  {
    key: "keplr",
    label: "Keplr",
    provider: "Keplr",
    icon: <WalletIcons.Keplr />,
    url: "https://www.keplr.app/",
  },
  // {
  //   key: "ledger",
  //   label: "Ledger",
  //   provider: "Ledger",
  //   icon: <WalletIcons.Vultisig />,
  // },
  {
    key: "leap",
    label: "Leap",
    provider: "Leap",
    icon: <WalletIcons.Leap />,
    url: "https://www.leapwallet.io/",
  },

  {
    key: "metamask",
    label: "Metamask",
    provider: "Metamask",
    icon: <WalletIcons.MetaMask />,
    url: "https://metamask.io/",
  },

  {
    key: "rabby",
    label: "Rabby",
    provider: "Rabby",
    icon: <WalletIcons.Rabby />,
    url: "https://rabby.io/",
  },

  {
    key: "okx",
    label: "OKX",
    provider: "Okx",
    icon: <WalletIcons.OKX className="color-white" />,
    url: "https://web3.okx.com",
  },
  // {
  //   key: "tronlink",
  //   label: "TronLink",
  //   provider: "Tronlink",
  //   icon: <WalletIcons.Tronlink />,
  //   url: "https://www.tronlink.org",
  // },
  {
    key: "trust-extenion",
    label: "Trust Extension",
    provider: "Trust",
    icon: <WalletIcons.Trust />,
    url: "https://trustwallet.com/browser-extension",
  },

  {
    key: "vultisig",
    label: "Vultisig",
    provider: "Vultisig",
    icon: <WalletIcons.Vultisig />,
    url: "https://vultisig.com",
  },
  // {
  //   key: "xaman",
  //   label: "Xaman",
  //   provider: "Xaman",
  //   icon: <WalletIcons.Xaman />,
  //   url: "https://xaman.app",
  // },
];

export const Header: FC = () => {
  return (
    <Suspense fallback={<HeaderLoading />}>
      <HeaderContent />
    </Suspense>
  );
};
export const HeaderContent: FC = () => {
  const { accountData, isLoading } = usePreloadedAccountData();
  const balances = usePreloadedBalanceData();
  const account = useFragment<PortfolioFragment$key>(
    PortfolioFragment,
    accountData
  );
  const { showModal } = useGlobalModalContext();
  const [deposits, , clearDeposit, clearAllDeposits] =
    usePreloadedPendingDeposits();
  const getValue = (): bigint =>
    account && balances?.ok
      ? totalValue(account, balances.value.edges) * 10000n
      : 0n;

  const accountProvider = useAccounts();
  const deposit = () => {
    showModal({
      title: ``,
      className: "modal--large",
      backgroundClose: false,
      children: <Deposit />,
    });
  };

  return (
    <BaseHeader
      accountProvider={{ ...accountProvider, isLoading }}
      routingElement={Link}
      ProviderIcon={ProviderIcon}
      wallets={WALLETS}
      onDeposit={deposit}
      deposits={{
        deposits,
        dismissDeposit: clearDeposit,
        clearAll: clearAllDeposits,
      }}
      getAccountValue={getValue}
    />
  );
};

const HeaderLoading: FC = () => {
  const { showModal } = useGlobalModalContext();
  const [deposits, , clearDeposit, clearAllDeposits] =
    usePreloadedPendingDeposits();

  const accountProvider = useAccounts();
  const deposit = () => {
    showModal({
      title: ``,
      className: "modal--large",
      backgroundClose: false,
      children: <Deposit />,
    });
  };

  return (
    <BaseHeader
      accountProvider={{ ...accountProvider, isLoading: true }}
      routingElement={Link}
      ProviderIcon={ProviderIcon}
      wallets={WALLETS}
      onDeposit={deposit}
      deposits={{
        deposits,
        dismissDeposit: clearDeposit,
        clearAll: clearAllDeposits,
      }}
      getAccountValue={() => 0n}
    />
  );
};

export const ProviderIcon: FC<{
  provider: Provider.Key;
  selected: boolean;
  className?: string;
}> = ({ provider, selected, className }) => {
  switch (provider) {
    case "Keplr":
      return selected ? (
        <WalletIcons.Keplr className={clsx({ [`${className}`]: className })} />
      ) : (
        <WalletIcons.KeplrSimple
          className={clsx({ [`${className}`]: className })}
        />
      );
    case "Leap":
      return selected ? (
        <WalletIcons.Leap className={clsx({ [`${className}`]: className })} />
      ) : (
        <WalletIcons.LeapSimple
          className={clsx({ [`${className}`]: className })}
        />
      );
    case "Ledger":
      return (
        <WalletIcons.Ledger
          className={clsx({
            [`${className}`]: className,
            "color-white": selected,
          })}
        />
      );
    case "Metamask":
      return selected ? (
        <WalletIcons.MetaMask
          className={clsx({ [`${className}`]: className })}
        />
      ) : (
        <WalletIcons.MetaMaskSimple
          className={clsx({ [`${className}`]: className })}
        />
      );
    case "Rabby":
      return selected ? (
        <WalletIcons.Rabby className={clsx({ [`${className}`]: className })} />
      ) : (
        <WalletIcons.RabbySimple
          className={clsx({ [`${className}`]: className })}
        />
      );
    // case "Sonar":
    //   return (
    //     <WalletIcons.SonarSimple
    //       className={clsx({[`${className}`]: className, "color-white": selected })}
    //     />
    //   );
    case "Station":
      return (
        <WalletIcons.StationSimple
          className={clsx({ [`${className}`]: className })}
          gradient={selected}
        />
      );
    case "Vultisig":
      return selected ? (
        <WalletIcons.Vultisig
          className={clsx({ [`${className}`]: className })}
        />
      ) : (
        <WalletIcons.VultisigSimple
          className={clsx({ [`${className}`]: className })}
        />
      );
    case "Ctrl":
      return (
        <WalletIcons.Ctrl
          className={clsx({
            [`${className}`]: className,
            "color-white": selected,
          })}
        />
      );
    case "DaoDao":
      return (
        <WalletIcons.Daodao
          className={clsx({
            [`${className}`]: className,
            "color-white": selected,
          })}
        />
      );
    case "Coinbase":
      return selected ? (
        <WalletIcons.Coinbase
          className={clsx({ [`${className}`]: className })}
        />
      ) : (
        <WalletIcons.CoinbaseSimple
          className={clsx({ [`${className}`]: className })}
        />
      );
    case "Brave":
      return selected ? (
        <WalletIcons.Brave className={clsx({ [`${className}`]: className })} />
      ) : (
        <WalletIcons.BraveSimple
          className={clsx({ [`${className}`]: className })}
        />
      );
    case "Okx":
      return (
        <WalletIcons.OKX
          className={clsx({
            [`${className}`]: className,
            "color-white": selected,
          })}
        />
      );
    case "Trust":
      return selected ? (
        <WalletIcons.Trust className={clsx({ [`${className}`]: className })} />
      ) : (
        <WalletIcons.TrustSimple
          className={clsx({ [`${className}`]: className })}
        />
      );
    case "Tronlink":
      return selected ? (
        <WalletIcons.Tronlink
          className={clsx({ [`${className}`]: className })}
        />
      ) : (
        <WalletIcons.TronlinkSimple
          className={clsx({ [`${className}`]: className })}
        />
      );
    case "Xaman":
      return selected ? (
        <WalletIcons.Xaman className={clsx({ [`${className}`]: className })} />
      ) : (
        <WalletIcons.XamanSimple
          className={clsx({ [`${className}`]: className })}
        />
      );
  }
};
